import React, { useState, useEffect, useMemo } from 'react';
import { X, Brain, Clock, Target, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useTasks } from '@/context/TaskContext';
import { useLists } from '@/context/ListContext';
import { useAuth } from '@/context/AuthContext';

interface Suggestion {
  id: string;
  type: 'priority' | 'time' | 'balance' | 'motivation';
  icon: React.ReactNode;
  title: string;
  message: string;
  actionable: boolean;
}

interface FocusCoachProps {
  tasks: any[];
}

const FocusCoach: React.FC<FocusCoachProps> = ({ tasks }) => {
  const { user } = useAuth();
  const { lists } = useLists();
  const [dismissedSuggestions, setDismissedSuggestions] = useState<string[]>([]);

  // Load dismissed suggestions from localStorage on mount
  useEffect(() => {
    if (user?.id) {
      const dismissed = localStorage.getItem(`focus-coach-dismissed-${user.id}`);
      if (dismissed) {
        setDismissedSuggestions(JSON.parse(dismissed));
      }
    }
  }, [user?.id]);

  // Save dismissed suggestions to localStorage
  const dismissSuggestion = (suggestionId: string) => {
    const newDismissed = [...dismissedSuggestions, suggestionId];
    setDismissedSuggestions(newDismissed);
    if (user?.id) {
      localStorage.setItem(`focus-coach-dismissed-${user.id}`, JSON.stringify(newDismissed));
    }
  };

  // Generate intelligent suggestions based on user's actual task data
  const suggestions = useMemo(() => {
    if (!tasks.length) return [];

    const allSuggestions: Suggestion[] = [];
    const today = new Date().toDateString();

    // 1. Priority Gap Analysis
    const tasksWithoutPriority = tasks.filter(task => 
      task.status !== 'done' && !task.priority_quadrant
    );
    
    if (tasksWithoutPriority.length >= 3) {
      allSuggestions.push({
        id: `priority-gap-${tasksWithoutPriority.length}`,
        type: 'priority',
        icon: <AlertTriangle className="w-4 h-4 text-amber-500" />,
        title: 'Priority Setup Needed',
        message: `You have ${tasksWithoutPriority.length} tasks with no priority set. Assign them to stay on track!`,
        actionable: true
      });
    }

    // 2. Time Allocation Analysis
    const highPriorityTasks = tasks.filter(task => 
      task.priority_quadrant === 'urgent-important' && task.status !== 'done'
    );
    const lowPriorityTasks = tasks.filter(task => 
      (task.priority_quadrant === 'not-urgent-not-important' || 
       task.priority_quadrant === 'urgent-not-important') && 
      task.totalTimeSpent > 60 // More than 1 hour spent
    );

    if (highPriorityTasks.length > 0 && lowPriorityTasks.length > 0) {
      const lowPriorityTime = lowPriorityTasks.reduce((sum, task) => sum + task.totalTimeSpent, 0);
      const hours = Math.round(lowPriorityTime / 3600 * 10) / 10;
      
      if (hours >= 1) {
        allSuggestions.push({
          id: `time-allocation-${hours}`,
          type: 'time',
          icon: <Clock className="w-4 h-4 text-blue-500" />,
          title: 'Time Allocation Alert',
          message: `You spent ${hours}hrs on low-priority tasks recently. Consider focusing on high-priority ones first.`,
          actionable: true
        });
      }
    }

    // 3. Task Balance Analysis
    if (lists.length > 0) {
      const overloadedLists = lists.map(list => {
        const listTasks = tasks.filter(task => 
          task.list_id === list.id && task.status !== 'done'
        );
        return { list, taskCount: listTasks.length };
      }).filter(item => item.taskCount >= 6);

      if (overloadedLists.length > 0) {
        const listName = overloadedLists[0].list.name;
        const taskCount = overloadedLists[0].taskCount;
        
        allSuggestions.push({
          id: `balance-${listName}-${taskCount}`,
          type: 'balance',
          icon: <Target className="w-4 h-4 text-purple-500" />,
          title: 'Task Load Management',
          message: `Your '${listName}' list has ${taskCount} pending tasks. Consider breaking them into smaller chunks to avoid overload.`,
          actionable: true
        });
      }
    }

    // 4. Motivation and Progress Recognition
    const completedTasks = tasks.filter(task => task.status === 'done');
    const completedHighPriority = completedTasks.filter(task => 
      task.priority_quadrant === 'urgent-important'
    );
    const totalTasks = tasks.filter(task => task.status !== 'done').length;

    if (completedHighPriority.length >= 2) {
      allSuggestions.push({
        id: `motivation-progress-${completedHighPriority.length}`,
        type: 'motivation',
        icon: <CheckCircle className="w-4 h-4 text-green-500" />,
        title: 'Great Progress!',
        message: `Excellent work completing ${completedHighPriority.length} high-priority tasks! Keep the momentum going.`,
        actionable: false
      });
    } else if (totalTasks === 0 && completedTasks.length > 0) {
      allSuggestions.push({
        id: `motivation-complete-${today}`,
        type: 'motivation',
        icon: <CheckCircle className="w-4 h-4 text-green-500" />,
        title: 'All Clear!',
        message: `Amazing! You've completed all your tasks. Time to set new goals or take a well-deserved break.`,
        actionable: false
      });
    } else if (allSuggestions.length === 0 && tasks.length > 0) {
      // Gentle encouragement when no major issues
      allSuggestions.push({
        id: `motivation-steady-${today}`,
        type: 'motivation',
        icon: <Brain className="w-4 h-4 text-indigo-500" />,
        title: 'Steady Progress',
        message: `Your task management looks good! Remember to take breaks and stay focused on what matters most.`,
        actionable: false
      });
    }

    // Filter out dismissed suggestions and limit to 3
    return allSuggestions
      .filter(suggestion => !dismissedSuggestions.includes(suggestion.id))
      .slice(0, 3);
  }, [tasks, lists, dismissedSuggestions]);

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">Focus Coach</h4>

      <div className="space-y-3">
        {suggestions.map((suggestion) => (
          <div key={suggestion.id} className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1">
                <div className="mt-0.5">
                  {suggestion.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                    {suggestion.title}
                  </h5>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    {suggestion.message}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 h-auto"
                onClick={() => dismissSuggestion(suggestion.id)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FocusCoach;